import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, InputAdornment, LinearProgress, MenuItem, Paper, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExtractoImportarDialog from "./ExtractoImportarDialog";
import ConciliacionPanel from "./ConciliacionPanel";
import MovimientoRelacionesDialog from "./MovimientoRelacionesDialog";
import EliminarMovimientoDialog from "./EliminarMovimientoDialog";
import EditarMovimientoDialog from "./EditarMovimientoDialog";
import { RelacionesDialog } from "./ConciliacionDialogs";
import RegistrarMovimientoDialog from "./RegistrarMovimientoDialog";
import { exportarMovimientosExtracto, fmtFecha, getBancosExtracto, listarMovimientosExtracto } from "../services/egresosService";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 32 },
  "& .MuiInputBase-input": { py: "5px", fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};
const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };
const montoLinkSx = { cursor: "pointer", fontWeight: 700, color: "primary.main", textDecoration: "underline dotted", "&:hover": { textDecoration: "underline" } };

const ESTADOS = {
  Correcto: { etiqueta: "Correcto", color: "success" },
  "No existe": { etiqueta: "Pendiente", color: "warning" },
  "Enlazado con Excel": { etiqueta: "Enlazado con Extracto", color: "primary" },
};

const OPCIONES_FILAS = [25, 50, 100, 200];
const monto = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Monto de una celda (texto de la BD): vacío si no hay, con miles y 2 decimales si es número
const celdaMonto = (v) => (v === "" || v === null || v === undefined ? "" : Number.isFinite(Number(v)) ? monto(v) : v);

// Tarjeta de resumen: etiqueta pequeña y cifra grande, con un filete de color a la izquierda
const Resumen = ({ etiqueta, valor, color, nota }) => (
  <Box sx={{ flex: "1 1 150px", minWidth: 150, px: 1.5, py: 0.9, border: 1, borderColor: "divider", borderLeft: 3, borderLeftColor: color, bgcolor: "background.paper" }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}>{valor}</Typography>
    {nota && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{nota}</Typography>}
  </Box>
);

// Extracto histórico del banco, mes a mes (espejo de extracto_historico/importar.php): lista
// los movimientos del mes con su estado de conciliación y permite importar el extracto desde Excel.
// "Correctos" y "No existen" son la conciliación (busqueda1 / busqueda3 del sistema viejo).
export default function ExtractoTab() {
  const hoy = new Date();
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [year, setYear] = useState(hoy.getFullYear());
  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [totales, setTotales] = useState({ debito: 0, credito: 0 });
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(50);
  const [refresco, setRefresco] = useState(0); // se incrementa para volver a pedir la página actual
  const [busqueda, setBusqueda] = useState(""); // texto ya con espera (lo que se envía al servidor)
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [filtro, setFiltro] = useState("");
  const [banco, setBanco] = useState(""); // "" = todos los bancos
  const [exportando, setExportando] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [importarOpen, setImportarOpen] = useState(false);
  const [vista, setVista] = useState("movimientos");
  const [relacion, setRelacion] = useState(null); // { movimiento, tipo }
  const [aEliminar, setAEliminar] = useState(null); // movimiento que se quiere eliminar
  const [aEditar, setAEditar] = useState(null); // movimiento que se quiere editar
  const [relId, setRelId] = useState(null); // movimiento cuyas relaciones se ven (y se pueden desvincular)
  const [registrarOpen, setRegistrarOpen] = useState(false); // formulario para registrar un movimiento a mano

  // El filtro espera un momento antes de consultar y siempre vuelve a la primera página.
  useEffect(() => {
    const t = setTimeout(() => { setBusqueda(filtro.trim()); setPagina(0); }, 350);
    return () => clearTimeout(t);
  }, [filtro]);

  // Excel del mes con el banco y la búsqueda que se estén usando
  const exportarExcel = async () => {
    setExportando(true);
    setError("");
    try {
      const resp = await exportarMovimientosExtracto({ mes, year, q: busqueda || undefined, banco: banco || undefined });
      const cab = resp.headers?.["content-disposition"] || "";
      const nombre = (/filename="?([^";]+)"?/.exec(cab) || [])[1] || `extracto_${mes}_${year}.xlsx`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      setError("No se pudo exportar el extracto a Excel.");
    } finally {
      setExportando(false);
    }
  };

  // Bancos que aparecen en el extracto, para el filtro
  useEffect(() => { getBancosExtracto(true).then(({ data }) => setBancos(Array.isArray(data) ? data : [])).catch(() => setBancos([])); }, [refresco]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await listarMovimientosExtracto({ mes, year, q: busqueda || undefined, banco: banco || undefined, page: pagina + 1, limit: limite });
      setFilas(data.rows || []);
      setTotal(data.total || 0);
      setTotales(data.totales || { debito: 0, credito: 0 });
    } catch {
      setFilas([]);
      setTotal(0);
      setError("No se pudo cargar el extracto del mes.");
    } finally {
      setCargando(false);
    }
  }, [mes, year, busqueda, banco, pagina, limite, refresco]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar(); }, [cargar]);

  // Si al eliminar un movimiento la página actual queda vacía (era la última), se vuelve a la anterior.
  useEffect(() => {
    if (!cargando && !filas.length && total > 0 && pagina > 0) setPagina(Math.max(Math.ceil(total / limite) - 1, 0));
  }, [cargando, filas.length, total, pagina, limite]);

  const visibles = filas;

  const anios = Array.from({ length: hoy.getFullYear() + 2 - 2016 }, (_, i) => 2016 + i);

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 1.5, overflow: "hidden" }}>
        {/* Título y acciones principales */}
        <Box sx={{ p: 1.75, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>Extracto histórico</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {MESES[mes - 1]} {year}
              {vista === "movimientos" && !cargando && ` · ${total} movimiento${total === 1 ? "" : "s"}`}
            </Typography>
          </Box>
          {vista === "movimientos" && (
            <Tooltip title="Descarga el extracto de este mes en Excel (con el banco y la búsqueda actuales)">
              <span>
                <Button variant="outlined" startIcon={exportando ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />} onClick={exportarExcel} disabled={exportando || !total} sx={{ textTransform: "none", fontWeight: 700 }}>Exportar Excel</Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Agrega un solo movimiento a mano">
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setRegistrarOpen(true)} sx={{ textTransform: "none", fontWeight: 700 }}>Registrar movimiento</Button>
          </Tooltip>
          <Tooltip title="Importa los movimientos desde el Excel del banco">
            <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setImportarOpen(true)} sx={{ textTransform: "none", fontWeight: 700 }}>Registrar extracto</Button>
          </Tooltip>
        </Box>

        <Divider />

        {/* Qué mes y qué vista se está mirando */}
        <Box sx={{ px: 1.75, py: 1.25, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.05 : 0.03) }}>
          <TextField select size="small" label="Mes" value={mes} onChange={(e) => { setMes(Number(e.target.value)); setPagina(0); }} sx={{ ...fieldSx, width: 140 }}>
            {MESES.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Año" value={year} onChange={(e) => { setYear(Number(e.target.value)); setPagina(0); }} sx={{ ...fieldSx, width: 100 }}>
            {anios.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Banco" value={banco} onChange={(e) => { setBanco(e.target.value); setPagina(0); }} sx={{ ...fieldSx, width: 150 }}>
            <MenuItem value="">Todos</MenuItem>
            {bancos.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </TextField>
          <ToggleButtonGroup
            size="small" exclusive value={vista} onChange={(_, v) => v && setVista(v)}
            sx={{ bgcolor: "background.paper", "& .MuiToggleButton-root": { py: 0.6, px: 1.75, fontSize: 13, textTransform: "none", fontWeight: 700 } }}
          >
            <ToggleButton value="movimientos">Movimientos</ToggleButton>
            <ToggleButton value="correctos" color="success">Correctos</ToggleButton>
            <ToggleButton value="pendientes" color="error">No existen</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flexGrow: 1 }} />
          {vista === "movimientos" && (
            <TextField
              size="small" placeholder="Buscar código, monto, banco…" value={filtro} onChange={(e) => setFiltro(e.target.value)}
              sx={{ ...fieldSx, bgcolor: "background.paper", width: { xs: "100%", sm: 300 } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          )}
        </Box>
        {cargando && <LinearProgress sx={{ height: 2 }} />}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setOk("")}>{ok}</Alert>}

      {vista !== "movimientos" && <ConciliacionPanel key={`${vista}-${mes}-${year}-${refresco}-${banco}`} mes={mes} year={year} vista={vista} banco={banco} />}

      {vista === "movimientos" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <Resumen etiqueta="Movimientos" valor={total} color="#546e7a" nota={busqueda ? "que coinciden con la búsqueda" : `en ${MESES[mes - 1]} ${year}`} />
          <Resumen etiqueta="Débitos" valor={monto(totales.debito)} color="#c62828" nota="salidas" />
          <Resumen etiqueta="Créditos" valor={monto(totales.credito)} color="#2e7d32" nota="entradas" />
          <Resumen etiqueta="Neto" valor={monto(totales.credito - totales.debito)} color="#0b6e6e" nota="créditos − débitos" />
        </Box>
      )}

      {vista === "movimientos" && (
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "62vh" }}>
          <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx, "& .MuiTableCell-stickyHeader": { bgcolor: "background.paper", borderBottom: 2, borderColor: "divider" } }}>
            <TableHead>
              <TableRow>
                {[["Fecha"], ["Banco"], ["Descripción / Referencia"], ["Código"], ["Débito", "right"], ["Crédito", "right"], ["ITF", "right"], ["Estado"], [""]].map(([h, al]) => (
                  <TableCell key={h || "acc"} align={al || "left"} sx={{ fontWeight: 800, whiteSpace: "nowrap", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3, color: "text.secondary" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!visibles.length ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 0 }}>
                    {cargando ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>Cargando…</Typography>
                    ) : busqueda ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>Ningún movimiento coincide con la búsqueda.</Typography>
                    ) : (
                      <Box sx={{ py: 4, display: "grid", justifyItems: "center", gap: 0.75, textAlign: "center" }}>
                        <Box sx={{ width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center", color: "text.secondary", bgcolor: (t) => alpha(t.palette.text.primary, 0.07) }}><InboxOutlinedIcon /></Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>No hay movimientos en {MESES[mes - 1]} {year}</Typography>
                        <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 440 }}>Importa el extracto del banco desde el Excel o agrega un movimiento a mano.</Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap", justifyContent: "center" }}>
                          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setRegistrarOpen(true)} sx={{ textTransform: "none", fontWeight: 700 }}>Registrar movimiento</Button>
                          <Button size="small" variant="contained" startIcon={<UploadFileIcon />} onClick={() => setImportarOpen(true)} sx={{ textTransform: "none", fontWeight: 700 }}>Registrar extracto</Button>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ) : visibles.map((r) => {
                const e = ESTADOS[r.estado] || { etiqueta: r.estado, color: "default" };
                return (
                  <TableRow key={r.id} hover sx={{ "&:nth-of-type(even)": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {fmtFecha(r.fecha)}
                      <Typography component="span" sx={{ display: "block", fontSize: 10, color: "text.disabled", lineHeight: 1 }}>#{r.id}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.banco}</TableCell>
                    <TableCell sx={{ maxWidth: 0, width: "40%", minWidth: 200 }}>
                      <Tooltip title={[r.descripcion, r.referencia].filter(Boolean).join(" · ")} enterDelay={600}>
                        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.descripcion || <Box component="span" sx={{ color: "text.disabled" }}>—</Box>}
                          {r.referencia && <Box component="span" sx={{ color: "text.secondary" }}>{` · ${r.referencia}`}</Box>}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{r.cod_transaccion}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", ...(r.debito ? { ...montoLinkSx, color: "error.main" } : {}) }} onClick={r.debito ? () => setRelacion({ movimiento: r, tipo: "debito" }) : undefined} title={r.debito ? "Ver registros relacionados" : undefined}>{celdaMonto(r.debito)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", ...(r.credito ? { ...montoLinkSx, color: "success.main" } : {}) }} onClick={r.credito ? () => setRelacion({ movimiento: r, tipo: "credito" }) : undefined} title={r.credito ? "Ver registros relacionados" : undefined}>{celdaMonto(r.credito)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>{Number(r.itf) ? celdaMonto(r.itf) : ""}</TableCell>
                    <TableCell><Chip size="small" color={e.color} label={e.etiqueta} sx={{ height: 18, fontSize: 10.5 }} /></TableCell>
                    <TableCell align="right" sx={{ py: 0 }}>
                      <Tooltip title="Ver relaciones y desvincular">
                        <IconButton size="small" aria-label={`Ver relaciones del movimiento ${r.id}`} onClick={() => setRelId(r.id)}><LinkOffIcon sx={{ fontSize: 17 }} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Editar movimiento">
                        <IconButton size="small" aria-label={`Editar el movimiento ${r.id}`} onClick={() => setAEditar(r)}><EditOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar movimiento (solo si no tiene relaciones)">
                        <IconButton size="small" aria-label={`Eliminar el movimiento ${r.id}`} onClick={() => setAEliminar(r)}><DeleteOutlineIcon sx={{ fontSize: 17 }} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, px: 1.5, borderTop: 1, borderColor: "divider" }}>
          <Typography sx={{ fontSize: 12, color: "text.secondary", flex: 1, minWidth: 220 }}>
            Toca un débito o un crédito para ver sus registros relacionados.
          </Typography>
          <TablePagination
            component="div" count={total} page={pagina} rowsPerPage={limite} rowsPerPageOptions={OPCIONES_FILAS}
            onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
            labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ "& .MuiTablePagination-toolbar": { minHeight: 40 } }}
          />
        </Box>
      </Paper>
      )}

      <MovimientoRelacionesDialog movimiento={relacion?.movimiento} tipo={relacion?.tipo} onClose={() => setRelacion(null)} />

      <RegistrarMovimientoDialog
        open={registrarOpen}
        mes={mes}
        year={year}
        onClose={() => setRegistrarOpen(false)}
        onGuardado={({ id, mes: m, year: y, periodo }) => {
          setRegistrarOpen(false);
          setOk(`Movimiento ${id} registrado en ${periodo}. Quedó como Pendiente: enlázalo en la pestaña «No existen».`);
          setMes(m); setYear(y); setPagina(0); setRefresco((n) => n + 1);
        }}
      />

      <EditarMovimientoDialog
        movimiento={aEditar}
        onClose={() => setAEditar(null)}
        onGuardado={(m) => { setAEditar(null); setOk(`Movimiento ${m.id} actualizado.`); setRefresco((n) => n + 1); }}
        onVerRelaciones={(id) => { setAEditar(null); setRelId(id); }}
      />

      <RelacionesDialog id={relId} onClose={() => setRelId(null)} onCambio={() => setRefresco((n) => n + 1)} />

      <EliminarMovimientoDialog
        movimiento={aEliminar}
        onClose={() => setAEliminar(null)}
        onEliminado={(m) => { setAEliminar(null); setOk(`Movimiento ${m.id} eliminado del extracto.`); setRefresco((n) => n + 1); }}
      />

      <ExtractoImportarDialog
        open={importarOpen}
        onClose={() => setImportarOpen(false)}
        mes={mes}
        year={year}
        onGuardado={(mensaje, m, y) => { setOk(mensaje); setMes(m); setYear(y); setPagina(0); setRefresco((n) => n + 1); }}
      />
    </Box>
  );
}
